import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Link, Clock, Users, ExternalLink } from "lucide-react";
import { API } from '../utils/constants';
import recipeCache from '../utils/recipeCache';
import { getCollectionDisplayTitle } from '../utils/collectionTitle';
import { getWithRetry, isNotFound, isRecipePrivate } from '../utils/fetchRetry';
import { getAuthConfig } from '../utils/authSession';
import { isInstructionHeading, stripInstructionListMarker, hasSingleInstructionStep, withoutSingleStepNumber } from '../utils/quickImportFormatting';
import PublicCTA from '../components/PublicCTA';
import NotFound from './NotFound';
import RecipeDetail from './RecipeDetail';
import BetaForm from '../BetaForm';
import SaveCreditLine from '../components/SaveCreditLine';
import SaveRecipeOverlay from '../components/SaveRecipeOverlay';
import ProfileAvatar from '../components/ProfileAvatar';
import { Bookmark } from 'lucide-react';
import '../components/recipeDetailTitleActions.css';

const PublicRecipeDetail = () => {
  const { username, recipeSlug, userId, recipeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Start from the cache when we can: right after the address bar swaps from
  // /recipe/:id to the pretty link, the recipe is already in hand — rendering
  // it on the first frame avoids the skeleton flash ("glitch") mid-navigation.
  const firstParam = username || userId;
  const secondParam = recipeSlug || recipeId;
  const cachedInitialRecipe =
    (firstParam && secondParam ? recipeCache.getBySlug(firstParam, secondParam) : null) ||
    (recipeId ? recipeCache.getById(recipeId) : null);
  const [recipe, setRecipe] = useState(cachedInitialRecipe);
  const [notFound, setNotFound] = useState(false);
  const [privateRecipe, setPrivateRecipe] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [profile, setProfile] = useState(() => (firstParam ? recipeCache.getProfile(firstParam) : null));
  const [loading, setLoading] = useState(!cachedInitialRecipe);

  // The route can change while this component stays mounted (link from one
  // recipe to another). Reset displayed state synchronously — before paint —
  // so the previous recipe, error screen, or skeleton never flashes for the
  // new URL.
  const routeKey = `${firstParam}/${secondParam}`;
  const [renderedRouteKey, setRenderedRouteKey] = useState(routeKey);
  if (renderedRouteKey !== routeKey) {
    setRenderedRouteKey(routeKey);
    setRecipe(cachedInitialRecipe);
    setProfile(firstParam ? recipeCache.getProfile(firstParam) : null);
    setNotFound(false);
    setPrivateRecipe(false);
    setLoadError(false);
    setLoading(!cachedInitialRecipe);
  }
  // Save-to-your-Wellby
  const [isSaved, setIsSaved] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [saveOverlayRecipe, setSaveOverlayRecipe] = useState(null);

  // Route changed under us (back/forward or a link to another recipe):
  // any open save overlay / waitlist belongs to the previous recipe — drop it
  // so it can't save the wrong one.
  useEffect(() => {
    setSaveOverlayRecipe(null);
    setShowWaitlist(false);
  }, [username, recipeSlug, userId, recipeId]);
  // Only trust a token once it has actually worked — a stale/expired
  // adminToken in localStorage must fall back to the waitlist flow.
  const [viewerVerified, setViewerVerified] = useState(false);

  // Filled bookmark if the logged-in viewer already saved this recipe
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token || !recipe?.id) return;
    axios.get(`${API}/recipes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const saved = (res.data || []).some(r => r.saved_from_recipe_id === recipe.id);
        if (saved) setIsSaved(true);
        setViewerVerified(true);
      })
      .catch(() => { /* token invalid/expired — treat viewer as logged out */ });
  }, [recipe?.id]);

  const handleSaveRecipe = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token || !viewerVerified) {
      setShowWaitlist(true);
      return;
    }
    if (isSaved) {
      toast.info("Already saved to your Wellby");
      return;
    }
    // Save-flow v3: open the save-edit overlay over the blurred recipe
    // (category required, edits optional). Nothing is created until they
    // hit Save; closing keeps this exact scroll position.
    setSaveOverlayRecipe(recipe);
  };

  useEffect(() => {
    // If the route changes (or the component unmounts) while a request is in
    // flight, responses for the old route must not overwrite the new one.
    let cancelled = false;
    const fetchFreshProfile = async (ownerId, profileKey) => {
      const endpoint = ownerId
        ? `${API}/profile?user_id=${encodeURIComponent(ownerId)}`
        : `${API}/u/${encodeURIComponent(profileKey)}`;
      const profileResponse = await getWithRetry(endpoint);
      recipeCache.setProfile(profileKey, profileResponse.data);
      return profileResponse.data;
    };

    const fetchRecipe = async () => {
      // Fresh request for a (possibly new) URL — clear stale outcome state.
      // Only show the skeleton when we don't already have a cached recipe to
      // render (cache-hit paths below resolve synchronously, so no flash).
      setNotFound(false);
      setPrivateRecipe(false);
      setLoadError(false);
      try {
        let recipeData;
        let profileData;
        
        // Check if we're using username/slug format or userId/recipeId format
        if (username && recipeSlug) {
          // Check cache first
          const cachedRecipe = recipeCache.getBySlug(username, recipeSlug);
          const cachedProfile = recipeCache.getProfile(username);
          
          if (cachedRecipe) {
            setRecipe(cachedRecipe);
            setLoading(false);
            if (cachedProfile) {
              setProfile(cachedProfile);
            }
            // A collection title or profile photo may have changed since this
            // recipe was cached. Revalidate from the owner's current profile
            // so the back-link always stays in sync with the collection page.
            fetchFreshProfile(cachedRecipe.user_id, username)
              .then((freshProfile) => {
                if (!cancelled) setProfile(freshProfile);
              })
              .catch(() => {});
            return;
          }

          setLoading(true);
          // New URL format: /username/recipe-slug
          // Send the login token when we have one: the owner's own PRIVATE
          // recipes also live under pretty URLs, and the server only shows
          // them when it knows who's asking. Without this, an owner clicking
          // their private recipe gets a bogus "not found" right after the
          // address bar swaps to the pretty link.
          const authCfg = await getAuthConfig();
          if (cancelled) return;
          const response = await getWithRetry(`${API}/u/${username}/${recipeSlug}`, authCfg);
          recipeData = response.data;
          
          // Cache the recipe
          recipeCache.set(recipeData, username);
          
          // Always prefer the owner's current profile. The cached copy remains
          // a best-effort fallback if the separate profile request hiccups.
          profileData = cachedProfile;
          try {
            profileData = await fetchFreshProfile(recipeData.user_id, username);
          } catch {}
        } else if (userId && recipeId) {
          // Check cache first
          const cached = recipeCache.getById(recipeId);
          if (cached) {
            setRecipe(cached);
            setLoading(false);
            const cachedProfile = recipeCache.getProfile(cached.user_id || userId);
            if (cachedProfile) setProfile(cachedProfile);
            fetchFreshProfile(cached.user_id || userId, cached.user_id || userId)
              .then((freshProfile) => {
                if (!cancelled) setProfile(freshProfile);
              })
              .catch(() => {});
            return;
          }

          setLoading(true);
          // Legacy URL format: /userId/recipeId
          const authCfg = await getAuthConfig();
          if (cancelled) return;
          const response = await getWithRetry(`${API}/recipes/${recipeId}`, authCfg);
          recipeData = response.data;
          recipeCache.set(recipeData);
          try {
            profileData = await fetchFreshProfile(
              recipeData.user_id || userId,
              recipeData.user_id || userId
            );
          } catch {}
        } else {
          // Treat first param as username and second as slug
          const firstParam = username || userId;
          const secondParam = recipeSlug || recipeId;
          
          // Check cache first
          const cachedRecipe = recipeCache.getBySlug(firstParam, secondParam);
          const cachedProfile = recipeCache.getProfile(firstParam);
          
          if (cachedRecipe) {
            setRecipe(cachedRecipe);
            setLoading(false);
            if (cachedProfile) {
              setProfile(cachedProfile);
            }
            fetchFreshProfile(cachedRecipe.user_id, firstParam)
              .then((freshProfile) => {
                if (!cancelled) setProfile(freshProfile);
              })
              .catch(() => {});
            return;
          }

          setLoading(true);
          // Try username/slug first
          try {
            const authCfg = await getAuthConfig();
            if (cancelled) return;
            const response = await getWithRetry(`${API}/u/${firstParam}/${secondParam}`, authCfg);
            recipeData = response.data;
            recipeCache.set(recipeData, firstParam);
            
            profileData = cachedProfile;
            try {
              profileData = await fetchFreshProfile(recipeData.user_id, firstParam);
            } catch {}
          } catch (slugError) {
            // Only fall back to the ID lookup when the slug genuinely
            // doesn't exist — a transient failure must surface as a retry
            // screen, not get converted into the fallback's 404.
            if (!isNotFound(slugError)) throw slugError;
            const authCfg = await getAuthConfig();
            if (cancelled) return;
            const response = await getWithRetry(`${API}/recipes/${secondParam}`, authCfg);
            recipeData = response.data;
            recipeCache.set(recipeData);
          }
        }
        
        if (cancelled) return;
        setRecipe(recipeData);
        setProfile(profileData);
      } catch (error) {
        if (cancelled) return;
        // Only a genuine 404 means "not found"; transient failures get a
        // retry screen instead of scaring people into thinking it's gone.
        if (isRecipePrivate(error)) {
          // Never leave owner-only data available after the server says this
          // viewer is unauthorized (notably after switching accounts).
          if (recipe?.id) recipeCache.invalidate(recipe.id);
          if (firstParam && secondParam) recipeCache.invalidateSlug(firstParam, secondParam);
          if (recipeId) recipeCache.invalidate(recipeId);
          setRecipe(null);
          setPrivateRecipe(true);
        } else if (isNotFound(error)) {
          setNotFound(true);
        } else {
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRecipe();
    return () => { cancelled = true; };
  }, [username, recipeSlug, userId, recipeId, navigate, retryKey]);

  const handleCopyLink = () => {
    const baseUrl = window.location.origin;
    navigator.clipboard.writeText(`${baseUrl}/${username}/${recipeSlug}`);
    toast.success("Link copied to clipboard!");
  };
  
  const handleBackToCollection = () => {
    // Use username from URL params first, then profile, then fallback
    if (username) {
      navigate(`/${username}`);
    } else if (profile?.username) {
      navigate(`/${profile.username}`);
    } else if (userId) {
      navigate(`/${userId}`);
    } else if (recipe?.user_id) {
      navigate(`/${recipe.user_id}`);
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }}>
        <div className="max-w-4xl mx-auto p-8">
          {/* Back button skeleton */}
          <div className="h-5 w-32 rounded mb-6" style={{ backgroundColor: '#D6D0C2' }} />

          <div className="rounded-lg" style={{ backgroundColor: '#F3EEE4' }}>
            {/* Image placeholder skeleton */}
            <div className="w-full h-64 md:h-80" style={{ backgroundColor: '#D6D0C2' }} />
            
            <div className="p-6">
              {/* Category tags skeleton */}
              <div className="flex gap-2 mb-4">
                <div className="h-6 w-16 rounded-full" style={{ backgroundColor: '#D6D0C2' }} />
                <div className="h-6 w-20 rounded-full" style={{ backgroundColor: '#D6D0C2' }} />
              </div>
              
              {/* Title skeleton */}
              <div className="h-9 w-3/4 rounded mb-2" style={{ backgroundColor: '#D6D0C2' }} />
              
              {/* Creator skeleton */}
              <div className="h-5 w-32 rounded mb-6" style={{ backgroundColor: '#D6D0C2' }} />
              
              {/* Meta info skeleton */}
              <div className="flex gap-6 mb-6">
                <div className="h-5 w-24 rounded" style={{ backgroundColor: '#D6D0C2' }} />
                <div className="h-5 w-24 rounded" style={{ backgroundColor: '#D6D0C2' }} />
              </div>
              
              {/* Description skeleton */}
              <div className="space-y-2 mb-8">
                <div className="h-4 w-full rounded" style={{ backgroundColor: '#D6D0C2' }} />
                <div className="h-4 w-5/6 rounded" style={{ backgroundColor: '#D6D0C2' }} />
              </div>
              
              {/* Ingredients section skeleton */}
              <div className="h-6 w-28 rounded mb-4" style={{ backgroundColor: '#D6D0C2' }} />
              <div className="space-y-2 mb-8">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-4 w-48 rounded" style={{ backgroundColor: '#D6D0C2' }} />
                ))}
              </div>
              
              {/* Instructions section skeleton */}
              <div className="h-6 w-28 rounded mb-4" style={{ backgroundColor: '#D6D0C2' }} />
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-4 w-full rounded" style={{ backgroundColor: '#D6D0C2' }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <NotFound
        title="Trouble loading this recipe"
        message="The recipe is safe — we just couldn't reach it. Check your connection and try again."
        onRetry={() => {
          setLoadError(false);
          setLoading(true);
          setRetryKey((k) => k + 1);
        }}
      />
    );
  }

  if (privateRecipe) {
    const signedIn = !!localStorage.getItem('adminToken');
    const returnTo = location.pathname + location.search;
    return (
      <NotFound
        title="Oops, this recipe is private and can only be viewed when signed in!"
        message={signedIn
          ? "Only the account that owns this recipe can view it. Sign in with the owner account to continue."
          : "If it’s yours, sign in with the account that owns it"}
        actionLabel="Sign In"
        onAction={() => navigate(`/?login=true&returnTo=${encodeURIComponent(returnTo)}`)}
      />
    );
  }

  if (notFound || !recipe) {
    return (
      <NotFound
        title="Recipe not found"
        message="This recipe doesn't exist or may have been deleted."
      />
    );
  }

  // If the signed-in owner is viewing their own recipe at the pretty URL,
  // show the full owner view (edit tools etc.) instead of the public view.
  const adminToken = localStorage.getItem('adminToken');
  if (adminToken) {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isOwner = recipe.user_id && user.id === recipe.user_id;
    if (isOwner) {
      return <RecipeDetail recipeId={recipe.id} />;
    }
  }

  const renderInstructions = () => {
    if (hasSingleInstructionStep(recipe.instructions)) {
      return <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} className="whitespace-pre-line">{withoutSingleStepNumber(recipe.instructions)}</p>;
    }
    if (recipe.instructions_type === "numbered") {
      const steps = recipe.instructions.split("\n").filter(s => s.trim());
      let stepNumber = 0;
      return (
        <ol className="list-decimal list-inside space-y-2" style={{ color: '#51120A' }}>
          {steps.map((step, idx) => {
            const isHeading = isInstructionHeading(step);
            if (!isHeading) stepNumber += 1;
            return (
              <li
                key={idx}
                className={isHeading ? "list-none font-semibold" : undefined}
                value={isHeading ? undefined : stepNumber}
                style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              >
                {stripInstructionListMarker(step)}
              </li>
            );
          })}
        </ol>
      );
    }
    return <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} className="whitespace-pre-wrap">{recipe.instructions}</p>;
  };

  // Build URLs for oEmbed and sharing
  const currentUrl = window.location.href;
  const baseUrl = window.location.origin;
  const oembedUrl = `${API}/oembed?url=${encodeURIComponent(currentUrl)}&format=json`;
  
  // Get image URL (only non-base64 for OG tags)
  const ogImageUrl = recipe.image_url && !recipe.image_url.startsWith('data:')
    ? recipe.image_url
    : `${API}/og-card?title=${encodeURIComponent(recipe.title || 'Recipe')}&category=${encodeURIComponent((recipe.categories && recipe.categories.length > 0) ? recipe.categories[0] : '')}`;
  const ingredientsList = (recipe.ingredients || []).map(i => typeof i === 'string' ? i : (i.name || i.text || '')).filter(Boolean).join(', ');

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }}>
      <Helmet>
        <title>{recipe.title} - Wellby</title>
        <meta name="description" content={recipe.description || `A recipe by ${recipe.creator || profile?.creator_name || 'Wellby User'}`} />
        
        {/* oEmbed Discovery */}
        <link rel="alternate" type="application/json+oembed" href={oembedUrl} title={recipe.title} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={recipe.title} />
        <meta property="og:description" content={ingredientsList || recipe.description || `A recipe by ${recipe.creator || 'Wellby User'}`} />
        <meta property="og:site_name" content="Wellby" />
        <meta property="og:image" content={ogImageUrl} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={recipe.title} />
        <meta name="twitter:description" content={ingredientsList || recipe.description || `A recipe by ${recipe.creator || 'Wellby User'}`} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>
      
      <div className="max-w-4xl mx-auto p-8">
        <button
          onClick={handleBackToCollection}
          className="text-link mb-6 inline-flex items-center gap-2 text-base"
          data-testid="back-to-collection-btn"
          style={{ fontFamily: "'Bricolage Grotesque', sans-serif", background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <ProfileAvatar
            photoUrl={profile?.creator_photo_url}
            name={profile?.creator_name || profile?.first_name || profile?.username || recipe?.creator}
            testId="back-to-collection-avatar"
          />
          {profile ? getCollectionDisplayTitle(profile) : 'View All Recipes'}
        </button>

        <Card style={{ backgroundColor: '#F3EEE4', boxShadow: 'none' }}>
          {/* Recipe Image */}
          {recipe.image_url && (
            <div className="w-full h-64 md:h-80 overflow-hidden rounded-t-xl">
              <img 
                src={recipe.image_url} 
                alt={recipe.title}
                className="w-full h-full object-cover"
                data-testid="public-recipe-detail-image"
              />
            </div>
          )}
          
          <CardHeader>
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
              <div className="flex-1">
                <div className="mb-2 flex flex-wrap gap-2">
                  {recipe.categories && recipe.categories.length > 0 && recipe.categories.map((cat, idx) => (
                    <span 
                      key={idx}
                      className="inline-block px-3 py-1 rounded-full text-sm font-medium"
                      style={{ backgroundColor: '#C9D7E9', color: '#0F2444', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                      data-testid={`recipe-detail-category-${idx}`}
                    >
                      {cat}
                    </span>
                  ))}
                </div>
                <div className="recipe-detail-title-row flex items-center gap-3 mb-2">
                  <CardTitle className="text-3xl" style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }} data-testid="recipe-detail-title">
                    {recipe.title}
                  </CardTitle>
                  {/* Save and copy anchored to the title — the same compact
                      actions used on recipe cards. */}
                  <div className="recipe-detail-title-actions flex items-center gap-1">
                    <button
                      onClick={handleSaveRecipe}
                      aria-label={isSaved ? "Saved to your Wellby" : "Save to your Wellby"}
                      className="flex items-center justify-center save-icon-btn focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#51120A] rounded"
                      style={{ background: 'transparent', border: 'none', padding: 0, flexShrink: 0, cursor: 'pointer' }}
                      title={isSaved ? "Saved to your Wellby" : "Save to your Wellby"}
                      data-testid="save-recipe-btn"
                    >
                      <Bookmark
                        className={`h-[26px] w-[26px] transition-colors ${isSaved ? 'fill-[#51120A]' : 'fill-none'}`}
                        strokeWidth={1.4}
                        style={{ color: '#51120A' }}
                      />
                    </button>
                    <button
                      onClick={handleCopyLink}
                      aria-label="Copy link to this recipe"
                      className="flex items-center justify-center copylink-btn focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#51120A] rounded"
                      style={{
                        width: '26px',
                        height: '28px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        padding: 0,
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                      title="Copy Link"
                      data-testid="copy-link-btn"
                    >
                      <span
                        className="copylink-ring flex items-center justify-center transition-colors"
                        style={{
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          border: '1.5px solid #51120A',
                          flexShrink: 0
                        }}
                      >
                        <Link className="h-4 w-4" strokeWidth={2.25} style={{ color: '#51120A' }} />
                      </span>
                    </button>
                  </div>
                </div>
                {/* Lead with where it was saved from; the original author follows */}
                <SaveCreditLine recipe={recipe} placement="detail" />
                {recipe.creator && (
                  <CardDescription className="text-lg" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="recipe-detail-creator">
                    {(recipe.saved_from_user_id && recipe.saved_from_name) ? 'Originally by' : 'by'} {recipe.creator}
                  </CardDescription>
                )}
              </div>
            </div>

            {(recipe.prep_time || recipe.cook_time || recipe.servings) && (
              <div className="flex gap-6 mt-4 pb-4 border-b" style={{ borderColor: '#D6D0C2' }}>
                {recipe.prep_time && (
                  <div className="flex items-center" style={{ color: '#51120A' }}>
                    <Clock className="h-5 w-5 mr-2" style={{ color: '#51120A' }} />
                    <div>
                      <div className="text-xs" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Prep Time</div>
                      <div className="font-medium" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="recipe-detail-prep-time">{recipe.prep_time}</div>
                    </div>
                  </div>
                )}
                {recipe.cook_time && (
                  <div className="flex items-center" style={{ color: '#51120A' }}>
                    <Clock className="h-5 w-5 mr-2" style={{ color: '#51120A' }} />
                    <div>
                      <div className="text-xs" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Cook Time</div>
                      <div className="font-medium" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="recipe-detail-cook-time">{recipe.cook_time}</div>
                    </div>
                  </div>
                )}
                {recipe.servings && (
                  <div className="flex items-center" style={{ color: '#51120A' }}>
                    <Users className="h-5 w-5 mr-2" style={{ color: '#51120A' }} />
                    <div>
                      <div className="text-xs" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Servings</div>
                      <div className="font-medium" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="recipe-detail-servings">{recipe.servings}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardHeader>

          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }}>Ingredients</h3>
                <div data-testid="recipe-detail-ingredients">
                  {recipe.ingredients?.map((ingredient, idx) => {
                    if (!ingredient || ingredient.trim() === "") {
                      return <div key={idx} className="h-3" />;
                    }
                    const cleaned = ingredient.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '');
                    const isHeader = /.*:\s*$/.test(cleaned.trim()) && !/^\d/.test(cleaned.trim());
                    if (isHeader) {
                      return (
                        <p key={idx} className="font-semibold mt-4 mb-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                          {cleaned.trim()}
                        </p>
                      );
                    }
                    return (
                      <div key={idx} className="flex items-start py-0.5">
                        <span className="inline-block rounded-full mt-2 mr-3 flex-shrink-0" style={{ width: '6px', height: '6px', backgroundColor: '#51120A' }}></span>
                        <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>{cleaned}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }}>Instructions</h3>
                <div data-testid="recipe-detail-instructions">
                  {renderInstructions()}
                </div>
              </div>

              {/* Notes */}
              {recipe.notes && (
                <div className="mt-6">
                  <h3 className="text-xl font-semibold mb-3" style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }}>Notes</h3>
                  <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} className="whitespace-pre-line" data-testid="recipe-notes">{recipe.notes}</p>
                </div>
              )}
            </div>

            {recipe.source_link && (
              <div className="mt-6 pt-6 border-t" style={{ borderColor: '#D6D0C2' }}>
                <a
                  href={recipe.source_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-link inline-flex items-center"
                  data-testid="recipe-detail-source-link"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Original Source
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quiet creator invitation — public recipe pages omit the collection
          page's divider so this reads as a signature, not another section. */}
      <PublicCTA showDivider={false} />

      <BetaForm
        open={showWaitlist}
        onOpenChange={setShowWaitlist}
        pendingSaveRecipe={recipe}
        onLoggedInForSave={(r) => setSaveOverlayRecipe(r)}
      />
      {saveOverlayRecipe && (
        <SaveRecipeOverlay
          original={saveOverlayRecipe}
          onClose={() => setSaveOverlayRecipe(null)}
          solidBackdrop
        />
      )}
    </div>
  );
};

export default PublicRecipeDetail;
